import React, {useMemo} from 'react';

import {ProcessFlowchartPlatformFeatureType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {
  NodeDetailsSidepanelContext,
  NodeDetailsSidepanelContextValue,
} from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/nodes/NodeDetailsSidepanelContext';
import FlowchartContextPanel, {
  PanelSide,
  SidepanelSize,
} from 'ui-library/reactflow/sidepanels/FlowchartContextPanel';

import {NodeDetailsSidepanelContentProps, SidepanelTab} from './processFlowchartConfigEditorConstants';
import {FlowchartNode, isInteractiveTaskNode} from './processFlowchartConfigEditorNodeTypes';
import {PROCESS_FLOWCHART_SIDEPANEL_TAB_BY_PLATFORM_FEATURE} from './processFlowchartConfigEditorRegistry';
import NodeDetailsSidepanelContent from './right-sidepanel/NodeDetailsSidepanelContent';

interface RightSidepanelProps
  extends Omit<NodeDetailsSidepanelContentProps, 'selectedNodeTaskDefinitionVersionSpec'>,
    Omit<NodeDetailsSidepanelContextValue, 'selectedNode'> {
  selectedNode: FlowchartNode | null;
  isOpen: boolean;
  platformFlowchartFeatures?: Map<ProcessFlowchartPlatformFeatureType, boolean>;
  onClose: () => void;
}

function getSidepanelTabsForPlatformFeatures(
  platformFlowchartFeatures?: Map<ProcessFlowchartPlatformFeatureType, boolean>
): Array<SidepanelTab> {
  if (platformFlowchartFeatures == null) {
    return [];
  }
  const platformFeatureSidepanelTabs = [];
  for (const [feature, enabled] of platformFlowchartFeatures) {
    if (enabled && PROCESS_FLOWCHART_SIDEPANEL_TAB_BY_PLATFORM_FEATURE[feature] != null) {
      platformFeatureSidepanelTabs.push(PROCESS_FLOWCHART_SIDEPANEL_TAB_BY_PLATFORM_FEATURE[feature]);
    }
  }
  return platformFeatureSidepanelTabs;
}

// TODO(BNCH-126721): Consolidate with transition modal right sidepanel
export default function ProcessFlowchartConfigEditorRightSidepanel(
  props: RightSidepanelProps
): JSX.Element | null {
  const {selectedNode, selectedFieldId, isOpen, onClose, ...contextProps} = props;

  const contextValue = useMemo((): NodeDetailsSidepanelContextValue | null => {
    if (!selectedNode) {
      return null;
    }

    return {
      ...contextProps,
      selectedNode,
    };
  }, [contextProps, selectedNode]);

  return (
    <FlowchartContextPanel
      panelSide={PanelSide.RIGHT}
      sidepanelName={`Node ${selectedNode?.data.nodeName} Details`}
      sidepanelIcon="settings"
      isOpen={isOpen && selectedNode != null}
      onClose={onClose}
      size={SidepanelSize.MEDIUM}
    >
      {contextValue && selectedNode && (
        <NodeDetailsSidepanelContext.Provider key={selectedNode.id} value={contextValue}>
          <div>
            {/* TODO(BNCH-126759): Add support for rendering router and end node configuration to sidepanel */}
            {isInteractiveTaskNode(selectedNode) ? (
              <NodeDetailsSidepanelContent
                nodeDetailsSidepanelContentProps={{
                  selectedNodeTaskDefinitionVersionSpec: selectedNode.data.taskDefinitionVersionSpec,
                  flowchart: props.flowchart,
                  selectedFieldId,
                  locked: props.locked,
                  onUpdateTaskDefinitionVersion: props.onUpdateTaskDefinitionVersion,
                  onChangeSelectedFieldId: props.onChangeSelectedFieldId,
                }}
                tabs={[
                  ...selectedNode.data.nodeDetailsSidepanelContent,
                  ...getSidepanelTabsForPlatformFeatures(props.platformFlowchartFeatures),
                ]}
              />
            ) : (
              <div>
                Placeholder sidepnael for {selectedNode.data.nodeType} - {selectedNode.data.nodeName}
              </div>
            )}
          </div>
        </NodeDetailsSidepanelContext.Provider>
      )}
    </FlowchartContextPanel>
  );
}
